import { useMemo } from "react";
import { Link } from "wouter";
import { ChevronRight, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, ItemRow, SectionHeading } from "./item-ui";
import { REVIEW_GROUPS, automationActive, gateOf, isNeedsFix, reviewReasonOf, verifyOf, vetoStateOf, type ExplainerContext, type PublishPolicy, type ReviewReason } from "./agentic";
import { KIND_GROUPS, fmtDateTime, humanizeCheckName, kindOf, toDate, type StudioItem } from "./helpers";

/** Drafts older than this are tucked into a collapsed "Older drafts" group so today's work leads. */
const OLDER_THAN_DAYS = 14;

export function sortNewestFirst(a: StudioItem, b: StudioItem): number {
  return (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0);
}

export interface ReviewEntry {
  item: StudioItem;
  reason: ReviewReason;
}

/**
 * The items that actually need a person: on hold, needing a fix, or waiting on a Manual channel.
 * Pieces flowing through a Veto or Automatic channel are not here; they publish by themselves.
 */
export function reviewList(items: StudioItem[], policy: PublishPolicy | null | undefined, workspace: any): ReviewEntry[] {
  const out: ReviewEntry[] = [];
  for (const item of items) {
    const reason = reviewReasonOf(item, policy, workspace);
    if (reason) out.push({ item, reason });
  }
  return out.sort((a, b) => sortNewestFirst(a.item, b.item));
}

/** Items in the order the screen shows them, so "Next item" walks down the page. */
export function reviewOrder(list: ReviewEntry[]): StudioItem[] {
  const out: StudioItem[] = [];
  for (const g of REVIEW_GROUPS) {
    const inGroup = list.filter((e) => e.reason === g.id).map((e) => e.item);
    if (g.id === "approve") for (const kg of KIND_GROUPS) out.push(...inGroup.filter((i) => kg.kinds.includes(kindOf(i))));
    else out.push(...inGroup);
  }
  return out;
}

/** A line saying why this one is here, for the groups where it is not obvious. */
function noteFor(entry: ReviewEntry): string | undefined {
  const { item, reason } = entry;
  if (reason === "held") return "You held this. Release it to publish, or reject it.";
  if (reason === "fix") {
    const verify = verifyOf(item);
    if (verify?.state === "pulled_back") return `Pulled back after it was published${verify.reason ? `: ${verify.reason}` : "."}`;
    const gate = gateOf(item);
    const failing = gate ? gate.checks.filter((c) => !c.passed && c.blocking !== false).map((c) => humanizeCheckName(c.name)) : [];
    if (failing.length) return `Check failed: ${failing.slice(0, 4).join(", ")}${failing.length > 4 ? ` and ${failing.length - 4} more` : ""}.`;
    return isNeedsFix(item) ? "Marked as needing a fix." : "The pre-publish check did not pass.";
  }
  return undefined;
}

function Rows({ entries, onOpen }: { entries: ReviewEntry[]; onOpen: (item: StudioItem) => void }) {
  return (
    <div className="space-y-2">
      {entries.map((e) => (
        <ItemRow
          key={e.item.id}
          item={e.item}
          onOpen={onOpen}
          showStage={e.reason === "approve" && vetoStateOf(e.item) === "none"}
          note={noteFor(e)}
          actions={
            <Button type="button" size="sm" variant="outline" onClick={() => onOpen(e.item)} className="w-full sm:w-auto" data-testid={`review-${e.item.id}`}>
              Review <ChevronRight size={14} />
            </Button>
          }
        />
      ))}
    </div>
  );
}

function Groups({ entries, onOpen, ctx }: { entries: ReviewEntry[]; onOpen: (item: StudioItem) => void; ctx: ExplainerContext }) {
  const groups = REVIEW_GROUPS.map((g) => ({ ...g, entries: entries.filter((e) => e.reason === g.id) })).filter((g) => g.entries.length > 0);
  return (
    <div className="space-y-8">
      {groups.map((g) => (
        <section key={g.id} className="space-y-3" data-testid={`group-${g.id}`}>
          <div className="space-y-0.5">
            <SectionHeading title={g.label} count={g.entries.length} />
            <p className="text-xs text-muted-foreground" data-testid={`explainer-${g.id}`}>{g.explainer(ctx)}</p>
          </div>
          {g.id === "approve" ? (
            <div className="space-y-5">
              {KIND_GROUPS.map((kg) => ({ ...kg, entries: g.entries.filter((e) => kg.kinds.includes(kindOf(e.item))) }))
                .filter((kg) => kg.entries.length > 0)
                .map((kg) => (
                  <div key={kg.id} className="space-y-2" data-testid={`group-${kg.id}`}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {kg.label} <span className="font-normal">{kg.entries.length}</span>
                    </h3>
                    <Rows entries={kg.entries} onOpen={onOpen} />
                  </div>
                ))}
            </div>
          ) : (
            <Rows entries={g.entries} onOpen={onOpen} />
          )}
        </section>
      ))}
    </div>
  );
}

/** A labelled rule between "new" and "already seen" drafts. */
function Divider({ children, tone = "amber", testId }: { children: React.ReactNode; tone?: "amber" | "muted"; testId: string }) {
  const line = tone === "amber" ? "bg-amber-500/40" : "bg-border";
  const text = tone === "amber" ? "text-amber-300" : "text-muted-foreground";
  return (
    <div role="separator" className="flex items-center gap-3" data-testid={testId}>
      <span className={`h-px flex-1 ${line}`} />
      <span className={`text-center text-xs font-semibold ${text}`}>{children}</span>
      <span className={`h-px flex-1 ${line}`} />
    </div>
  );
}

export function InboxTab({
  entries: pending,
  loading,
  onOpen,
  lastVisit = null,
  policy,
  workspace,
  policyKnown,
  onGoCalendar,
}: {
  entries: ReviewEntry[];
  loading: boolean;
  onOpen: (item: StudioItem) => void;
  /** Epoch ms of the viewer's previous visit (per-viewer convenience from localStorage); null when unknown. */
  lastVisit?: number | null;
  policy: PublishPolicy | null;
  workspace: any;
  policyKnown: boolean;
  onGoCalendar: () => void;
}) {
  const { fresh, recent, older } = useMemo(() => {
    const cutoff = Date.now() - OLDER_THAN_DAYS * 86_400_000;
    const f: ReviewEntry[] = [];
    const r: ReviewEntry[] = [];
    const o: ReviewEntry[] = [];
    for (const e of pending) {
      const t = toDate(e.item.createdAt)?.getTime() ?? Date.now();
      if (lastVisit !== null && t > lastVisit) f.push(e);
      // Held and needs-fix pieces are never tucked away as "older": they are blocked on you.
      else (t < cutoff && e.reason === "approve" ? o : r).push(e);
    }
    return { fresh: f, recent: r, older: o };
  }, [pending, lastVisit]);

  const automatic = automationActive(policy, workspace);
  const ctx: ExplainerContext = { policyKnown, automatic };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="inbox-loading">
        <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inbox-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground" data-testid="inbox-count">
            {pending.length === 0 ? "Nothing waiting for you" : `${pending.length} waiting for your decision`}
          </h2>
          {automatic && (
            <p className="text-sm text-muted-foreground" data-testid="inbox-subtitle">
              Only pieces that need a person are listed. Pieces on a Veto or Automatic channel publish by themselves; you can see and hold them on the Calendar.
            </p>
          )}
        </div>
        <Link
          href="/draper"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-foreground/90 hover:border-blue-500/40"
          data-testid="link-ask-draper"
        >
          <MessageSquare size={14} /> Ask Draper for something
        </Link>
      </div>

      {pending.length === 0 ? (
        <EmptyState title="Nothing waiting. Ask Draper for something.">
          <Link href="/draper" className="text-blue-400 underline underline-offset-2">Open the Draper chat</Link>
          {automatic && (
            <>
              {" "}or{" "}
              <button type="button" onClick={onGoCalendar} className="text-blue-400 underline underline-offset-2" data-testid="link-empty-calendar">
                see what is publishing on the Calendar
              </button>
            </>
          )}
        </EmptyState>
      ) : (
        <>
          {lastVisit !== null && fresh.length > 0 && (
            <>
              <Divider testId="divider-new">
                New since your last visit · {fresh.length} ({fmtDateTime(lastVisit)})
              </Divider>
              <Groups entries={fresh} onOpen={onOpen} ctx={ctx} />
              {(recent.length > 0 || older.length > 0) && <Divider tone="muted" testId="divider-earlier">Earlier</Divider>}
            </>
          )}
          {lastVisit !== null && fresh.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="inbox-nothing-new">
              Nothing new since your last visit ({fmtDateTime(lastVisit)}).
            </p>
          )}
          {recent.length > 0 && <Groups entries={recent} onOpen={onOpen} ctx={ctx} />}
          {older.length > 0 && (
            <details className="group rounded-xl border border-border bg-card/40" data-testid="group-older" open={recent.length === 0}>
              <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                <ChevronRight size={16} className="text-muted-foreground transition-transform group-open:rotate-90" />
                Older drafts
                <span className="font-normal text-muted-foreground">
                  {older.length} from more than {OLDER_THAN_DAYS} days ago
                </span>
              </summary>
              <div className="border-t border-border p-3 sm:p-4">
                <Groups entries={older} onOpen={onOpen} ctx={ctx} />
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
